import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnDestroy, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { take } from 'rxjs';
import { Task } from '../../shared/entities/task/task';
import { EditSelectV2InputComponent } from '../../../core/components/edit-select-v2-form-control/edit-select-v2-form-control.component';
import { WidgetCoordinatorService } from '../../../core/services/issue/widget-coordinator.service';
import { Location as LocationEntity } from '../../../core/entities/location/location';
import { CommandInput, CommandTemplate } from '../../../core/entities/commandTemplate/commandTemplate';
import { CommandService, Reloadable } from '../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { LocationService } from '../../shared/services/location/location.service';

@Component({
  selector: 'nx-update-task-location-widget',
  templateUrl: './update-task-location-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateTaskLocationWidgetComponent implements Reloadable, OnDestroy {

  // Injected services
  private readonly commandService = inject(CommandService);
  private readonly coordinator = inject(WidgetCoordinatorService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locationService = inject(LocationService);

  // Signal inputs
  readonly task = input.required<Task>();

  // View child signal
  readonly selectComponent = viewChild<EditSelectV2InputComponent>('selectComponent');

  // State signals
  readonly editMode = signal(false);
  readonly isLoading = signal(false);
  readonly commandTemplate = signal<CommandTemplate | undefined>(undefined);
  readonly activeLocation = signal<LocationEntity | null>(null);
  readonly allLocations = signal<LocationEntity[]>([]);
  readonly options = signal<LocationEntity[]>([]);

  // Form controls
  readonly selectedControl = new FormControl<LocationEntity | null>(null);
  readonly form = new FormGroup({
    selected: this.selectedControl
  });

  // Constants
  readonly placeholder = 'Niederlassung auswählen';

  // Widget ID for coordinator
  private readonly widgetId = `update-task-location-widget-${Math.random().toString(36).substr(2, 9)}`;
  private unregisterRefetch?: () => void;

  constructor() {
    // Subscribe to cancel all requests
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          this.cancel();
        }
      });

    // Effect to handle task changes and load command template
    effect(() => {
      const currentTask = this.task();
      if (currentTask?.id) {
        // Register with RefetchCoordinator in untracked to avoid circular dependencies
        untracked(() => {
          this.unregisterRefetch?.();
          this.unregisterRefetch = this.refetchCoordinator.register(
            this,
            currentTask.getEntityType(),
            currentTask.id
          );
        });

        // Load command template when task changes
        this.loadCommandTemplate();
      }
    });
  }

  loadCommandTemplate() {
    const currentTask = this.task();
    if (!currentTask?.id) return;

    const commandInput = new CommandInput();
    commandInput.name = 'taskUpdateLocationCommand';
    commandInput.addParameter('commandProviderId', currentTask.id);

    this.commandService.getCommandByCommandInput(commandInput)
      .pipe(take(1))
      .subscribe(result => {
        const template = result.data.getCommandByCommandInput;
        this.commandTemplate.set(template);

        const locations = LocationEntity.createLocations(
          template?.flow?.settings?.customFlowSettings?.locations
        );
        this.allLocations.set(locations);
        this.options.set(locations);

        const locationData = template?.flow?.settings?.customFlowSettings?.location;
        if (locationData) {
          const location = new LocationEntity(locationData);
          this.activeLocation.set(location);
          this.selectedControl.patchValue(location);
        } else {
          this.activeLocation.set(null);
          this.selectedControl.patchValue(null);
        }

        this.isLoading.set(false);
      });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    this.editMode.set(true);

    setTimeout(() => {
      this.selectComponent()?.openAndFocusFilter();
    }, 10);
  }

  save() {
    this.editMode.set(false);

    const currentTask = this.task();
    const template = this.commandTemplate();
    const selectedLocation = this.selectedControl.value;

    if (!currentTask?.id || !template?.name) return;

    const commandInput = new CommandInput();
    commandInput.name = template.name;
    commandInput.addParameter('commandProviderId', currentTask.id);
    commandInput.addParameter('locationId', selectedLocation?.id ?? null);

    this.isLoading.set(true);
    this.commandService.sendCommand(commandInput, (result) => {
      this.loadCommandTemplate();
      this.refetchCoordinator.emit({
        entityType: currentTask.getEntityType(),
        entityId: currentTask.id
      });
    });
  }

  cancel() {
    this.editMode.set(false);
    this.selectedControl.patchValue(this.activeLocation());
  }

  onFilter = (filterValue: string) => {
    // Remote filter - can be extended if needed
  };

  reload(): void {
    this.loadCommandTemplate();
  }
}
